import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import onlineImage from '../../assets/image/online5.jpg';

interface Exam {
  id: string;
  orderId: string;
  title: string;
  description: string;
  icon: string;
  color: string;
  questions: number;
  duration: string;
  hasCompleted?: boolean;
  applicationDate?: string;
  expiryDate?: string;
}

export default function SelectExam() {
  // Use a custom navigate wrapper if available (window.REACT_APP_NAVIGATE) or fall back to window.location logic if needed,
  // but since we are in a component, we can just use hooks if we were using useNavigate.
  // However, this file doesn't import useNavigate. Let's add it or use window.location.
  // Wait, I can see imports. I should check imports. 
  // Line 1: import { useState, useEffect } from 'react';
  // It lacks useNavigate. I'll stick to window.location for nav but ADD logic to save to storage.
  // Actually, better to add useNavigate.

  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const gradients = [
    'from-orange-200/60 to-rose-200/60',
    'from-purple-200/60 to-pink-200/60',
    'from-green-200/60 to-emerald-200/60',
    'from-pink-200/60 to-rose-200/60',
    'from-blue-200/60 to-cyan-200/60',
    'from-amber-200/60 to-yellow-200/60'
  ];

  const icons = [
    'ri-home-heart-line',
    'ri-flask-line',
    'ri-leaf-line',
    'ri-palette-line',
    'ri-heart-pulse-line',
    'ri-restaurant-line'
  ];

  const { userId } = useAuth();

  useEffect(() => {
    const fetchQualifications = async () => {
      try {
        if (!userId) {
          setLoading(false);
          return;
        }

        // Get user's active orders
        const userRef = doc(db, 'User', userId);

        // Query by UserRef (Reference)
        console.log('Fetching orders for UserRef:', userRef.path);
        const ordersQuery = query(
          collection(db, 'Order'),
          where('UserRef', '==', userRef),
          where('status', '==', 'active')
        );

        const snapshot = await getDocs(ordersQuery);
        const orderDocs = snapshot.docs;

        if (orderDocs.length === 0) {
          setExams([]);
          setLoading(false);
          return;
        }

        // Fetch Shikaku details
        const examsData = await Promise.all(
          orderDocs.map(async (orderDoc: any, index: number) => {
            const orderData = orderDoc.data();
            const shikakuDoc = await getDoc(orderData.shikakuId);
            if (!shikakuDoc.exists()) return null;

            const examData = shikakuDoc.data() as any;

            // Check if exam is finished
            let hasCompleted = false;
            if (orderData.isExamFinished) {
              hasCompleted = true;
            } else {
              // Check if already taken (AnswerSheet exists)
              const answerSheetQuery = query(
                collection(db, 'AnswerSheet'),
                where('UserRef', '==', userRef),
                where('ShikakuRef', '==', orderData.shikakuId)
              );
              const answerSheetSnapshot = await getDocs(answerSheetQuery);
              hasCompleted = !answerSheetSnapshot.empty;
            }

            return {
              id: shikakuDoc.id,
              orderId: orderDoc.id,
              title: examData.ShikakuName || shikakuDoc.id,
              description: '資格試験のオンラインテストです。途中で中断はできませんのでご注意ください。',
              icon: icons[index % icons.length],
              color: gradients[index % gradients.length],
              questions: 50,
              duration: '制限なし',
              hasCompleted,
              applicationDate: '2025年10月26日',
              expiryDate: '2027年10月26日'
            };
          })
        );

        const validExams = examsData.filter(exam => exam !== null) as Exam[];
        setExams(validExams);
      } catch (error) {
        console.error("Error fetching qualifications: ", error);
      } finally {
        setLoading(false);
      }
    };

    fetchQualifications();
  }, [userId]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="text-lg text-gray-600">読み込み中...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-sm shadow-sm sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-4 h-14 flex items-center">
          <button
            onClick={() => window.history.back()}
            className="mr-4 p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <i className="ri-arrow-left-line text-xl text-gray-700"></i>
          </button>
          <h1 className="text-xl font-bold text-gray-800 flex-1 text-center">資格試験を選択</h1>
          <div className="w-10"></div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-8">
        {exams.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500 text-lg">
              利用可能な資格試験がありません
            </div>
            <div className="text-gray-400 text-sm mt-2">
              管理者にお問い合わせください
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {exams.map((exam) => (
              <div
                key={exam.id}
                onClick={() => {
                  localStorage.setItem('selectedExam', JSON.stringify(exam));
                  navigate(`/exam-instructions?examId=${exam.id}`);
                }}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden group"
              >
                <div className="relative overflow-hidden">
                  <img
                    src={onlineImage}
                    alt="オンラインテスト"
                    className="w-full h-auto"
                  />
                  {exam.hasCompleted && (
                    <div className="absolute top-4 right-4 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                      受験済み
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{exam.title}</h3>
                  <p className="text-gray-600 text-sm mb-4">{exam.description}</p>
                  <div className="flex items-center justify-between text-sm text-gray-500">
                    <div className="flex items-center gap-2">
                      <i className="ri-file-list-line"></i>
                      <span>{exam.questions}問</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <i className="ri-time-line"></i>
                      <span>{exam.duration}</span>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
